import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, RotateCcw, ArrowRight, Loader2 } from "lucide-react";
import { Slider } from "../ui/slider";
import { trackPreview } from "./previewApi";

const fmt = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const PreviewPlayer = ({ src, song, fadeStart = 106 }) => {
  const audioRef = useRef(null);
  const playedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ended, setEnded] = useState(false);
  const [sounding, setSounding] = useState(false);
  const [error, setError] = useState(false);
  const retriesRef = useRef(0);
  const showCta = ended || time >= fadeStart;
  const loading = playing && !sounding && !error;

  const onAudioError = () => {
    const a = audioRef.current;
    setSounding(false);
    if (a && retriesRef.current < 2) {
      retriesRef.current += 1;
      setTimeout(() => { a.load(); a.play().catch(() => {}); }, 3000);
      return;
    }
    setPlaying(false);
    setError(true);
  };

  const retry = () => {
    const a = audioRef.current;
    if (!a) return;
    retriesRef.current = 0;
    setError(false);
    a.load();
    a.play().catch(() => {});
  };

  useEffect(() => () => audioRef.current?.pause(), []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => {});
      if (!playedRef.current) {
        playedRef.current = true;
        trackPreview("play");
      }
    } else a.pause();
  };

  const restart = () => {
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  return (
    <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-6 sm:p-8 shadow-[0_24px_60px_-20px_hsl(var(--primary)/0.55)]" data-testid="preview-player">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
      <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70">Sample song</p>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl font-bold leading-tight" data-testid="preview-song-title">
        {song.title}
      </h2>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {song.course_name && <span className="rounded-full bg-white/15 px-3 py-1" data-testid="preview-song-course">{song.course_name}</span>}
        {song.topic_title && song.topic_title !== song.title && <span className="rounded-full bg-white/15 px-3 py-1">{song.topic_title}</span>}
      </div>

      <div className="mt-8 flex items-center gap-5">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause sample song" : "Play sample song"}
          className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-white text-primary shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
          data-testid="preview-play-btn"
        >
          {loading ? <Loader2 className="h-7 w-7 animate-spin" /> : playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 translate-x-0.5" />}
        </button>
        <div className="flex h-10 items-end gap-1" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span
              key={i}
              className={`w-1.5 rounded-full bg-white/80 ${sounding ? "animate-bounce" : ""}`}
              style={{ height: `${30 + ((i * 37) % 70)}%`, animationDelay: `${i * 90}ms`, animationDuration: "900ms" }}
            />
          ))}
        </div>
        <button type="button" onClick={restart} aria-label="Restart song" className="ml-auto rounded-full p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white" data-testid="preview-restart-btn">
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-6">
        <Slider
          value={[time]}
          max={duration || 1}
          step={1}
          aria-label="Seek"
          onValueChange={([v]) => { if (audioRef.current) audioRef.current.currentTime = v; setTime(v); }}
          className="[&_[role=slider]]:border-white [&_[role=slider]]:bg-white [&>span:first-child]:bg-white/25 [&>span:first-child>span]:bg-white"
          data-testid="preview-seek"
        />
        <div className="mt-2 flex justify-between text-xs tabular-nums text-white/75">
          <span data-testid="preview-current-time">{fmt(time)}</span>
          <span data-testid="preview-duration">{fmt(duration)}</span>
        </div>
        <p className="mt-1 text-[11px] text-white/75" data-testid="preview-clip-note">
          {loading ? "Loading the song…" : "Preview clip · full song available in Bar Songz"}
        </p>
        {error && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-white/10 px-4 py-3 text-sm" role="alert" data-testid="preview-audio-error">
            <span>The song couldn't load. Please try again.</span>
            <button type="button" onClick={retry} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary" data-testid="preview-audio-retry-btn">Try again</button>
          </div>
        )}
      </div>

      {showCta && (
        <div className="mt-6 rounded-2xl bg-white/10 p-4 backdrop-blur-sm animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="preview-full-song-cta">
          <p className="text-sm text-white/85">{ended ? "That's the end of the preview." : "The preview is wrapping up…"}</p>
          <Link
            to="/"
            onClick={() => trackPreview("cta_click")}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-primary transition-transform duration-200 hover:scale-[1.03]"
            data-testid="preview-full-song-btn"
          >
            Hear the full song in Bar Songz <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => { setPlaying(true); setEnded(false); setError(false); }}
        onPlaying={() => { setSounding(true); retriesRef.current = 0; }}
        onWaiting={() => setSounding(false)}
        onPause={() => { setPlaying(false); setSounding(false); }}
        onError={onAudioError}
        onEnded={() => { setPlaying(false); setSounding(false); setEnded(true); trackPreview("complete"); }}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        data-testid="preview-audio"
      />
    </div>
  );
};
